import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  connectSocket,
  disconnectSocket,
  getSocket,
} from "../services/socketService";

const LiveStatusContext = createContext(null);

/** ===== Config (fallbacks) ===== */
const GRACE_MS_FALLBACK = 2 * 60 * 1000; // 2 minutes
const TICK_MS = 1000;
const IDLE_TICK_MS = 15000;

const LS_ENDED_AT = "live:endedAt";
const LS_GRACE_ENDS_AT = "live:graceEndsAt";

/** Compute current phase from server-first data, with robust fallbacks. */
function computePhase(nowMs, s, graceMsFallback) {
  if (!s) return "OFFLINE";

  const isTrulyLive =
    !!s.videoId &&
    (s.isLive === true || s.status === "live" || s.lifeCycleStatus === "live");

  // Only treat as LIVE when YT/broadcast says "live"
  if (isTrulyLive) return "LIVE";

  // explicitly completed → OFFLINE, regardless of any old flags
  if (s.status === "completed" || s.lifeCycleStatus === "complete") {
    return "OFFLINE";
  }

  const endedFlag =
    s.status === "ended" ||
    s.lifeCycleStatus === "completeUnsuccessful" ||
    s.lifeCycleStatus === "revoked";

  // Prefer explicit graceEndsAt from server
  const endsAt = s.graceEndsAt ? Date.parse(s.graceEndsAt) : NaN;
  if (!Number.isNaN(endsAt)) {
    if (nowMs < endsAt) return "ENDED_GRACE";
    return "OFFLINE";
  }

  // Fallback: endedAt + graceMs
  const endedRef = s.endedAt || null;
  const graceMs = s.graceMs ?? graceMsFallback;
  if (endedRef) {
    const endedAt = Date.parse(endedRef);
    if (!Number.isNaN(endedAt)) {
      const age = nowMs - endedAt;
      if (age < graceMs) return "ENDED_GRACE";
      return "OFFLINE";
    }
  }

  if (endedFlag) return "ENDED_GRACE";
  return "OFFLINE";
}

export function LiveStatusProvider({ children, auth }) {
  const [status, setStatus] = useState({
    isLive: false,
    videoId: null,
    title: null,
    thumbnail: null,
    startedAt: null,
    endedAt: null,
    status: null,
    privacy: null,
    lifeCycleStatus: null,
    channelId: null,
    lastUpdatedAt: null,

    // HYBRID additions
    graceMs: null,
    graceEndsAt: null,
    serverTime: null,
  });
  const [connected, setConnected] = useState(false);

  const [now, setNow] = useState(Date.now());
  const [skewMs, setSkewMs] = useState(0);

  // Rehydrate persisted end markers
  useEffect(() => {
    const bootEndedAt = localStorage.getItem(LS_ENDED_AT);
    const bootGraceEndsAt = localStorage.getItem(LS_GRACE_ENDS_AT);
    if (bootEndedAt || bootGraceEndsAt) {
      setStatus((prev) => ({
        ...prev,
        endedAt: prev.endedAt || bootEndedAt || null,
        graceEndsAt: prev.graceEndsAt || bootGraceEndsAt || null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist latest end markers
  useEffect(() => {
    try {
      if (status?.endedAt) localStorage.setItem(LS_ENDED_AT, status.endedAt);
      if (status?.graceEndsAt)
        localStorage.setItem(LS_GRACE_ENDS_AT, status.graceEndsAt);
    } catch {}
  }, [status?.endedAt, status?.graceEndsAt]);

  // Apply incoming snapshot + skew
  const applySnapshot = (snapshot) => {
    const clientReceipt = Date.now();
    const serverNow = snapshot?.serverTime
      ? Date.parse(snapshot.serverTime)
      : NaN;
    if (!Number.isNaN(serverNow)) {
      setSkewMs(serverNow - clientReceipt);
    }
    setStatus((prev) => {
      const snap = snapshot || {};

      const merged = {
        ...prev,
        ...snap,

        // 🔒 IMPORTANT: respect explicit nulls from server
        endedAt: Object.prototype.hasOwnProperty.call(snap, "endedAt")
          ? snap.endedAt
          : (prev.endedAt ?? null),

        graceEndsAt: Object.prototype.hasOwnProperty.call(snap, "graceEndsAt")
          ? snap.graceEndsAt
          : (prev.graceEndsAt ?? null),

        lastUpdatedAt: snap.lastUpdatedAt || new Date().toISOString(),
      };

      // 🔐 If backend says we're done, force isLive false
      if (
        merged.status === "ended" ||
        merged.status === "completed" ||
        merged.lifeCycleStatus === "complete"
      ) {
        merged.isLive = false;
      }

      // 🧹 Cleanup persistence when stream fully completes
      if (merged.status === "completed") {
        try {
          localStorage.removeItem(LS_ENDED_AT);
          localStorage.removeItem(LS_GRACE_ENDS_AT);
        } catch {}
      }

      return merged;
    });
  };

  const baseNow = useMemo(() => Date.now() + skewMs, [skewMs, now]);
  const phase = useMemo(
    () => computePhase(baseNow, status, GRACE_MS_FALLBACK),
    [baseNow, status],
  );

  const graceRemainingMs = useMemo(() => {
    if (status?.graceEndsAt) {
      const ends = Date.parse(status.graceEndsAt);
      if (!Number.isNaN(ends)) return Math.max(ends - baseNow, 0);
    }
    const endedRef = status?.endedAt;
    if (endedRef) {
      const endedAt = Date.parse(endedRef);
      if (!Number.isNaN(endedAt)) {
        const graceMs = status?.graceMs ?? GRACE_MS_FALLBACK;
        return Math.max(endedAt + graceMs - baseNow, 0);
      }
    }
    return 0;
  }, [status?.graceEndsAt, status?.endedAt, status?.graceMs, baseNow]);

  useEffect(() => {
    const interval = phase === "ENDED_GRACE" ? TICK_MS : IDLE_TICK_MS;
    const id = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(id);
  }, [phase]);

  const shouldConnect = !!auth?.isAuthenticated;

  useEffect(() => {
    if (!shouldConnect) {
      disconnectSocket();
      setConnected(false);
      return;
    }

    const socket = connectSocket();

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));

    const onSnapshot = (snapshot) => {
      applySnapshot(snapshot);
    };
    socket.on("liveStatus", onSnapshot);

    // Bootstrap snapshot (authoritative)
    socket.timeout(2000).emit("getLiveStatus", (err, snapshot) => {
      if (!err && snapshot) applySnapshot(snapshot);
    });

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("liveStatus", onSnapshot);
      disconnectSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldConnect]);

  const refresh = () => {
    const socket = getSocket();
    socket?.timeout(2000).emit("getLiveStatus", (err, snapshot) => {
      if (!err && snapshot) applySnapshot(snapshot);
    });
  };

  const value = useMemo(
    () => ({
      ...status,
      connected,
      phase, // "LIVE" | "ENDED_GRACE" | "OFFLINE"
      graceRemainingMs,
      graceMs: status?.graceMs ?? GRACE_MS_FALLBACK,
      refresh,
    }),
    [status, connected, phase, graceRemainingMs],
  );

  return (
    <LiveStatusContext.Provider value={value}>
      {children}
    </LiveStatusContext.Provider>
  );
}

export const useLiveStatus = () => useContext(LiveStatusContext);
